import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Zap, Plane, Ticket, Music, ArrowRight, MapPin, Loader2 } from "lucide-react";
import EventCard from "../components/EventCard";
import Footer from "../components/Footer";
import EventArtistSearch from "../components/EventArtistSearch";
import CitySearchAutocomplete from "../components/CitySearchAutocomplete";
import MobileSearchBar from "../components/MobileSearchBar";
import SkeletonCard from "../components/SkeletonCard";
import { toast } from "sonner";
import { useAudio } from "../contexts/AudioContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = ({ onSignUpClick }) => {
  const navigate = useNavigate();
  const { stopAll } = useAudio(); // Audio singleton - stop on filter change
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [searchMode, setSearchMode] = useState("event"); // "event" or "city"
  const [activeGenre, setActiveGenre] = useState(null); // Track active genre filter
  const [activeCity, setActiveCity] = useState(null); // Track active city filter
  const [availableCities, setAvailableCities] = useState([]); // Cities with active events

  // Check viewport size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // AUTO-POPULATE: Fetch events on mount immediately
  useEffect(() => {
    fetchFeaturedEvents();
    fetchAvailableCities();
  }, []);

  // Fetch cities with active events (for destination tags)
  const fetchAvailableCities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/cities`);
      const data = await response.json();
      if (data.cities) {
        setAvailableCities(data.cities.slice(0, 8)); // Top 8 cities
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchFeaturedEvents = async (genre = null, city = null) => {
    setLoading(true);
    try {
      // Use unified feed endpoint with Guy J prioritization
      let apiUrl = `${API_URL}/api/events/unified?limit=20`;
      if (genre) {
        apiUrl += `&genre=${encodeURIComponent(genre)}`;
      }
      if (city) {
        apiUrl += `&city=${encodeURIComponent(city)}`;
      }
      
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      if (data.events && data.events.length > 0) {
        setFeaturedEvents(data.events);
      } else if (!genre && !city) {
        // Fallback to regular endpoint and seed if empty
        const fallbackResponse = await fetch(`${API_URL}/api/events?limit=20`);
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.events && fallbackData.events.length > 0) {
          setFeaturedEvents(fallbackData.events);
        } else {
          await fetch(`${API_URL}/api/seed`, { method: 'POST' });
          const retryResponse = await fetch(`${API_URL}/api/events?limit=20`);
          const retryData = await retryResponse.json();
          setFeaturedEvents(retryData.events || []);
        }
      } else {
        // No events found for this filter combination
        setFeaturedEvents([]);
        const filterDesc = [genre, city].filter(Boolean).join(' in ');
        toast.info(`No ${filterDesc} events found. Try another filter.`);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  // Handle genre filter click - combined filter: (ActiveGenre && ActiveCity)
  // AUDIO: Stop all audio on filter change (no ghost music)
  const handleGenreFilter = (genre) => {
    stopAll(); // Audio singleton - stop on filter change
    if (activeGenre === genre) {
      // Clicking same filter clears it
      setActiveGenre(null);
      fetchFeaturedEvents(null, activeCity);
    } else {
      setActiveGenre(genre);
      fetchFeaturedEvents(genre, activeCity);
    }
  };

  // Handle city/destination filter click - combined filter: (ActiveGenre && ActiveCity)
  // AUDIO: Stop all audio on filter change (no ghost music)
  const handleCityFilter = (city) => {
    stopAll(); // Audio singleton - stop on filter change
    if (activeCity === city) {
      // Clicking same filter clears it
      setActiveCity(null);
      fetchFeaturedEvents(activeGenre, null);
    } else {
      setActiveCity(city);
      fetchFeaturedEvents(activeGenre, city);
    }
  };

  // Clear all filters
  // AUDIO: Stop all audio on filter clear
  const clearAllFilters = () => {
    stopAll(); // Audio singleton - stop on filter change
    setActiveGenre(null);
    setActiveCity(null);
    fetchFeaturedEvents(null, null);
  };

  // Handle event/artist selection from search
  const handleSearchSelect = (selection) => {
    stopAll(); // Audio singleton - stop on navigation
    if (selection.type === "event") {
      navigate(`/events/${selection.data.event_id}`);
    } else if (selection.type === "artist") {
      navigate(`/events?artist=${encodeURIComponent(selection.data)}`);
    }
  };

  // Handle city selection
  const handleCitySelect = (cityData) => {
    navigate(`/events?city=${encodeURIComponent(cityData.city)}`);
  };

  const handleSearchSubmit = () => {
    navigate('/events');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Event-First Discovery */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1920&q=80" 
            alt="Underground club experience"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/60 to-background" />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6 mb-12">
            <div className="inline-flex items-center gap-2">
              <span className="genre-badge text-secondary border-secondary">
                EVENT-FIRST TRAVEL
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-6xl lg:text-8xl font-black tracking-tight">
              <span className="text-foreground">Travel for</span>
              <br />
              <span className="text-primary">the Night.</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Find the event. We'll handle the rest.
              <span className="block mt-1 text-sm italic text-primary/70">
                Flights, hotels, and transfers—all in one checkout.
              </span>
            </p>
          </div>

          {/* Search Mode Toggle + Search Bar */}
          <div className="max-w-3xl mx-auto mb-12">
            {/* Search Mode Toggle */}
            <div className="flex justify-center gap-2 mb-4">
              <button
                onClick={() => setSearchMode("event")}
                className={`px-4 py-2 rounded-lg text-sm font-mono uppercase tracking-wider transition-all ${
                  searchMode === "event" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
                data-testid="search-mode-event"
              >
                <Music className="w-4 h-4 inline-block mr-2" />
                Search Events
              </button>
              <button
                onClick={() => setSearchMode("city")}
                className={`px-4 py-2 rounded-lg text-sm font-mono uppercase tracking-wider transition-all ${
                  searchMode === "city" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
                data-testid="search-mode-city"
              >
                <MapPin className="w-4 h-4 inline-block mr-2" />
                Browse by City
              </button>
            </div>

            <div className="glass-strong rounded-2xl p-2 border border-border/50 shadow-2xl">
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Search Input - toggles between event and city */}
                <div className="flex-1 bg-background/50 rounded-xl">
                  {searchMode === "event" ? (
                    <EventArtistSearch 
                      onSelect={handleSearchSelect}
                      placeholder="Search events, artists, or DJs..."
                    />
                  ) : (
                    <CitySearchAutocomplete 
                      onSelect={handleCitySelect}
                      placeholder="Search city (50+ UK & Europe)..."
                      navigateOnSelect={true}
                    />
                  )}
                </div>
                <Button 
                  onClick={handleSearchSubmit}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow h-14 px-8 uppercase tracking-wider font-bold rounded-xl group"
                  data-testid="hero-search-btn"
                >
                  {searchMode === "event" ? "Find Events" : "Browse City"}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 font-mono">
              XTrip Curated • Live event data from 50+ cities across UK & Europe
            </p>
          </div>

          {/* Quick Sub-Genre Filters - RA-Standard */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {[
              { label: 'Techno', icon: '⚡' },
              { label: 'Deep House', icon: '🏠' },
              { label: 'Melodic', icon: '🎹' },
              { label: 'Warehouse', icon: '🏭' },
              { label: 'Minimal', icon: '◯' },
              { label: 'Underground', icon: '🌑' }
            ].map((genre) => (
              <Button
                key={genre.label}
                variant="outline"
                size="sm"
                onClick={() => handleGenreFilter(genre.label)}
                className={`uppercase tracking-wider text-xs group transition-all ${
                  activeGenre === genre.label 
                    ? 'border-primary bg-primary/20 text-primary' 
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
                data-testid={`genre-filter-${genre.label.toLowerCase().replace(' ', '-')}`}
              >
                <span className="mr-1.5 group-hover:scale-110 transition-transform">{genre.icon}</span>
                {genre.label}
                {activeGenre === genre.label && (
                  <span className="ml-2 text-[10px]">✕</span>
                )}
              </Button>
            ))}
          </div>

          {/* Destination Tags - Cities with Active Events */}
          {availableCities.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-16">
              <span className="text-xs text-muted-foreground uppercase tracking-wider mr-2 self-center" style={{ fontFamily: "'Space Mono', monospace" }}>
                DESTINATIONS:
              </span>
              {availableCities.map((cityData) => (
                <Button
                  key={cityData.city}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCityFilter(cityData.city)}
                  className={`text-xs group transition-all px-3 py-1 h-auto ${
                    activeCity === cityData.city 
                      ? 'bg-secondary/20 text-secondary border border-secondary' 
                      : 'text-muted-foreground hover:text-secondary hover:bg-secondary/10'
                  }`}
                  style={{ fontFamily: "'Space Mono', monospace" }}
                  data-testid={`city-filter-${cityData.city.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <MapPin className="w-3 h-3 mr-1" />
                  {cityData.city}
                  <span className="ml-1 text-[10px] opacity-60">({cityData.count})</span>
                  {activeCity === cityData.city && (
                    <span className="ml-1 text-[10px]">✕</span>
                  )}
                </Button>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-xl mx-auto">
            {[
              { value: 'Live', label: 'EVENTS' },
              { value: '50+', label: 'CITIES' },
              { value: '1', label: 'CHECKOUT' }
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-mono text-2xl sm:text-3xl text-primary font-bold">{stat.value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-muted-foreground rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="genre-badge text-primary border-primary mb-4 inline-block">THE XTRIP WAY</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
              Effortless, end-to-end
            </h2>
            <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
              We handle the logistics. You live the moment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Ticket,
                title: 'Choose Your Experience',
                description: 'Hand-picked events across Europe\'s most iconic venues and hidden gems.',
                color: 'primary'
              },
              {
                icon: Zap,
                title: 'We Craft Your Journey',
                description: 'Flights, boutique stays, and transfers—tailored to your preferences.',
                color: 'secondary'
              },
              {
                icon: Plane,
                title: 'One Seamless Booking',
                description: 'Everything confirmed in minutes. No tabs, no hassle, no compromise.',
                color: 'accent'
              }
            ].map((step, index) => (
              <div 
                key={step.title}
                className="bg-background border border-border p-8 card-hover group"
              >
                <div className={`w-14 h-14 bg-${step.color}/10 border border-${step.color} flex items-center justify-center mb-6`}>
                  <step.icon className={`w-7 h-7 text-${step.color}`} />
                </div>
                <div className="font-mono text-xs text-muted-foreground mb-2">0{index + 1}</div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Events - Wall of Sound */}
      <section className="py-24 pb-32 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8 md:mb-12">
            <div>
              <span className="genre-badge text-secondary border-secondary mb-4 inline-block">UPCOMING</span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Curated Experiences
              </h2>
              <p className="text-muted-foreground mt-2 text-sm md:text-base">
                The moments that define a summer. Limited availability.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/events')}
              className="hidden sm:flex border-border text-muted-foreground hover:border-primary hover:text-primary uppercase tracking-wider"
            >
              View All
            </Button>
          </div>

          {loading ? (
            <>
              {/* Loading State - Space Mono "Wall of Sound Building" */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-muted/30 border border-border">
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  <span 
                    className="text-sm text-muted-foreground uppercase tracking-wider"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    {activeGenre ? `Loading ${activeGenre} events...` : 'Loading Warehouse Data...'}
                  </span>
                </div>
              </div>
              {/* Mobile Skeleton - Feed View */}
              <div className="md:hidden space-y-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <SkeletonCard key={i} variant="feed" />
                ))}
              </div>
              {/* Desktop Skeleton - Wall of Sound Grid */}
              <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
                  <SkeletonCard key={i} variant="default" />
                ))}
              </div>
            </>
          ) : featuredEvents.length === 0 ? (
            <div className="text-center py-16">
              <div 
                className="text-lg text-muted-foreground mb-4"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                No events found {activeGenre ? `for "${activeGenre}"` : ''}{activeCity ? ` in ${activeCity}` : ''}
              </div>
              <Button 
                variant="outline" 
                onClick={clearAllFilters}
                className="border-primary text-primary hover:bg-primary/10"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile Feed - Vertical Scrollable */}
              <div className="md:hidden space-y-3">
                {featuredEvents.map((event) => (
                  <EventCard key={event.event_id} event={event} variant="feed" />
                ))}
              </div>
              {/* Desktop - Wall of Sound CSS Grid */}
              <div 
                className="hidden md:grid gap-4 lg:gap-5"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gridAutoRows: 'minmax(320px, auto)'
                }}
                data-testid="wall-of-sound-grid"
              >
                {featuredEvents.map((event, index) => (
                  <EventCard key={event.event_id} event={event} index={index} variant="grid" />
                ))}
              </div>
            </>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Button
              onClick={() => navigate('/events')}
              className="bg-primary text-primary-foreground w-full uppercase tracking-wider"
            >
              View All Experiences
            </Button>
          </div>
        </div>
      </section>

      {/* Mobile Search Bar - Thumb Zone */}
      <MobileSearchBar onSelect={handleSearchSelect} isVisible={true} />

      {/* CTA Section */}
      <section className="py-24 bg-card border-y border-border relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient opacity-50" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
            Life&apos;s too short for ordinary weekends
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join discerning travellers who refuse to settle. 
            Premium experiences, effortlessly delivered.
          </p>
          <Button
            onClick={onSignUpClick}
            className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow px-12 h-14 uppercase tracking-wider font-bold text-lg"
            data-testid="cta-signup-btn"
          >
            Join the Waitlist
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
